import React, { useEffect, useState } from "react"; // Import useEffect and useState
import { createPortal } from "react-dom"; // Import createPortal
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import MobileOffcanvas from "./MobileOffcanvas";
import { useGetCartQuery } from "../../../cart/api/cartApi"; // Import cart query
import {
  toggleMobileOffcanvas,
  closeMobileOffcanvas,
  selectIsMobileOffcanvasOpen,
  openHeaderActionsOffcanvas, // Import action to open the other offcanvas
} from "../../headerUiSlice";
import type { AppDispatch } from "../../../../app/store";

// Simple Hamburger Icon SVG
const HamburgerIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mobile-action-icon hamburger-icon"
  >
    <line x1="3" y1="12" x2="21" y2="12"></line>
    <line x1="3" y1="6" x2="21" y2="6"></line>
    <line x1="3" y1="18" x2="21" y2="18"></line>
  </svg>
);

// Search Icon SVG (Copied from HeaderActionsContainer)
const SearchIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mobile-action-icon search-icon"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
  </svg>
);

// Cart Icon SVG (Copied from HeaderActionsContainer)
const CartIcon = ({ count }: { count?: number }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="mobile-action-icon cart-icon"
  >
    <circle cx="9" cy="21" r="1"></circle>
    <circle cx="20" cy="21" r="1"></circle>
    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
  </svg>
);

const MobileActionsContainer: React.FC = () => {
  const { t, ready: translationsReady } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const isMobileOffcanvasOpen = useSelector(selectIsMobileOffcanvasOpen);
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null); // State for portal target

  // Fetch cart data for the count indicator
  const { data: cart, isLoading: isCartLoading } = useGetCartQuery(undefined, {
    selectFromResult: ({ data, isLoading }) => ({
      data: data ? { itemsCount: data.itemsCount } : undefined,
      isLoading,
    }),
  });
  const cartItemCount = cart?.itemsCount ?? 0;

  const handleToggleMobileMenu = () => {
    dispatch(toggleMobileOffcanvas());
  };

  const handleCloseMobileMenu = () => {
    dispatch(closeMobileOffcanvas());
  };

  // Handler to open the HeaderActions offcanvas to the cart tab
  const handleOpenCartOffcanvas = () => {
    dispatch(openHeaderActionsOffcanvas("cart")); // Open the other offcanvas
    dispatch(closeMobileOffcanvas()); // Close this one
  };

  // Handler to open the HeaderActions offcanvas to the search tab
  const handleOpenSearchOffcanvas = () => {
    dispatch(openHeaderActionsOffcanvas("search")); // Open the other offcanvas
    dispatch(closeMobileOffcanvas()); // Close this one
  };

  // Effect to find the portal target element on mount
  useEffect(() => {
    const targetElement = document.getElementById("portal-mobile-menu-trigger");
    setPortalTarget(targetElement);
  }, []);

  // Effect to close mobile offcanvas if viewport becomes larger than desktop breakpoint
  useEffect(() => {
    const desktopBreakpoint = 1024; // Match $breakpoint-desktop in SCSS
    const mediaQuery = window.matchMedia(
      `(min-width: ${desktopBreakpoint + 1}px)`
    );

    const handleViewportChange = () => {
      // If viewport is wider than breakpoint (container is hidden) AND offcanvas is open
      if (mediaQuery.matches && isMobileOffcanvasOpen) {
        console.log(
          "Viewport wider than mobile breakpoint, closing mobile offcanvas."
        );
        dispatch(closeMobileOffcanvas());
      }
    };

    // Initial check on mount
    handleViewportChange();

    // Add listener for changes
    mediaQuery.addEventListener("change", handleViewportChange);

    // Cleanup listener on unmount
    return () => {
      mediaQuery.removeEventListener("change", handleViewportChange);
    };
  }, [isMobileOffcanvasOpen, dispatch]); // Depend on state and dispatch

  // Only render the component if translations are ready
  if (!translationsReady) {
    return null;
    // Or <div className="mobile-actions-loading">Loading...</div>
  }

  // Hamburger Button JSX
  const hamburgerButton = (
    <button
      className="mobile-action-button mobile-menu-toggle"
      aria-label={t("header.toggleMobileMenu", "Toggle mobile menu")}
      aria-expanded={isMobileOffcanvasOpen} // Indicate expanded state
      onClick={handleToggleMobileMenu} // Use specific handler
    >
      <HamburgerIcon />
    </button>
  );

  return (
    <>
      <div className="mobile-actions">
        {/* Search Button */}
        <button
          className="mobile-action-button mobile-search-toggle" // Use specific class if needed
          aria-label={t("common.search")}
          onClick={handleOpenSearchOffcanvas} // Use the new handler
        >
          <SearchIcon />
        </button>

        {/* Cart Button remains here */}
        <button
          className="mobile-action-button mobile-cart-toggle" // Use specific class if needed
          aria-label={t("cart.title")}
          onClick={handleOpenCartOffcanvas} // Use the new handler
        >
          <CartIcon count={cartItemCount} />
          {/* Cart Count Indicator */}
          {!isCartLoading && cartItemCount > 0 && (
            <span className="cart-count-badge">{cartItemCount}</span>
          )}
        </button>
        {/* Hamburger Menu Button is rendered via portal below */}
        {/* Add other mobile-specific buttons here if needed later */}
      </div>

      {/* Render Hamburger Button into the portal target if it exists */}
      {portalTarget && createPortal(hamburgerButton, portalTarget)}

      {/* Mobile Offcanvas Component */}
      <MobileOffcanvas
        isOpen={isMobileOffcanvasOpen}
        onClose={handleCloseMobileMenu} // Use specific handler
      >
        {/* Optional: Content for the mobile offcanvas can be added here */}
      </MobileOffcanvas>
    </>
  );
};

export default MobileActionsContainer;
